import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Car, Shield, Calendar, CalendarDays, Sparkles, Search, X } from 'lucide-react';
import { ref as dbRef, get } from 'firebase/database';
import { database } from '../config/firebase';
import { useCompanyBySlug } from '../hooks/useCompanyBySlug';
import { useCars } from '../hooks/useCars';
import { useCompanyPageSEO } from '../hooks/useCompanyPageSEO';
import { useAvailability } from '../hooks/useAvailability';
import { useSettings } from '../hooks/useSettings';
import { PLATFORM_HOSTNAMES } from '../config/platformHostnames';
import { CAR_CATEGORIES, MOTO_CATEGORIES, STATI_BLOCCANTI } from '../config/constants';
import { getEffectiveEndDate, parseLocalDate, toLocalISODate } from '../utils/helpers';
import CarCard from '../components/CarCard';
import CarsPagination from '../components/CarsPagination';
import BrandDefaultIcon from '../components/BrandDefaultIcon';
import LanguageToggle from '../components/LanguageToggle';
import BookingModal from '../components/BookingModal';
import PuntoCombobox from '../components/public/PuntoCombobox';
import { SEDE_ID, buildSedePoint, getActivePunti, eurosToCents, centsToEuros } from '../utils/puntiRitiro.generated';
import ProfileModal from '../components/ProfileModal';
import HeroSection, { getReadableTextColor } from '../components/public/HeroSection';
import ReviewsSection from '../components/public/ReviewsSection';
import PublicFooter from '../components/public/PublicFooter';

const MotoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
    <path d="M15 6l-2 4H8.5L7 13.5" /><path d="M15 6h3l1.5 4H15" />
    <path d="M8.5 10L6 17.5" /><path d="M15 10l1.5 4" />
  </svg>
);

/**
 * PublicCarList — Fase 5 del piano (sito pubblico multi-tenant).
 *
 * CAMBIAMENTI rispetto alla versione single-tenant:
 *
 * 1. Riceve companySlug (dalla rotta /:companySlug in App.js), risolto in
 *    companyId tramite useCompanyBySlug. Se lo slug non esiste, mostra una
 *    pagina "azienda non trovata" invece di crashare o mostrare dati vuoti.
 *
 * 2. Se subscriptionStatus è 'expired' o 'cancelled' (Fase 8/9 — abbonamento
 *    scaduto e periodo di grazia esaurito), il sito mostra un messaggio di
 *    indisponibilità invece del catalogo — coerente con la decisione presa
 *    nel documento di piano ("blocco totale di pannello e sito pubblico").
 *
 * 3. Il conflict-check di disponibilità NON legge più l'intero nodo bookings
 *    (che ora, giustamente, un cliente non-admin non può leggere secondo le
 *    Rules della Fase 1), ma usa useAvailability — l'indice senza dati
 *    personali creato apposta per questo (fix della falla di privacy).
 *
 * 4. Header e branding sono dinamici: nome/logo dell'azienda letti da
 *    useSettings(companyId), non più "NoleggioinCloud" hardcoded come brand
 *    del noleggio stesso (che resta invece nel footer, come attribuzione
 *    della piattaforma, non del singolo autonoleggio).
 *
 * 5. Le prenotazioni sono disabilitate (con messaggio esplicativo) se
 *    stripeConnected è false — l'azienda non ha ancora collegato Stripe.
 *
 * 6. Navigazione interna (login, contatti, privacy) tramite React Router
 *    (<Link>/useNavigate), non più callback props — dato che tutte queste
 *    rotte vivono nello stesso bundle dell'app (a differenza dei link verso
 *    il sito marketing, che restano <a> normali).
 */
const PublicCarList = ({ companySlug: propSlug, user }) => {
  const { companySlug: paramSlug } = useParams();
  const companySlug = propSlug || paramSlug;
  const navigate = useNavigate();

  // Su dominio personalizzato il routing è "pulito" (tuonoleggio.it/account,
  // senza slug — vedi CustomDomainSite in App.js), quindi tutti i link interni
  // vanno costruiti senza prefisso slug. Su dominio piattaforma invece lo slug
  // fa parte del path (/:companySlug/account) e va incluso, altrimenti i link
  // punterebbero fuori dalla rotta dell'azienda corrente.
  const isCustomDomain =
    typeof window !== 'undefined' && !PLATFORM_HOSTNAMES.has(window.location.hostname);
  const basePath = isCustomDomain ? '' : `/${companySlug}`;

  const { companyId, ragioneSociale, subscriptionStatus, accountBlocked, stripeConnected, loading: companyLoading, notFound } =
    useCompanyBySlug(companySlug);

  const { cars: allCars, loading: carsLoading } = useCars(companyId, { realtime: false });
  // Un veicolo può essere "disponibile" (bookabile) ma comunque nascosto dal
  // catalogo pubblico tramite l'interruttore "Visibile nel catalogo" nel
  // pannello admin (CarFormModal). Di default (campo assente) è visibile,
  // per compatibilità con i veicoli creati prima dell'introduzione del campo.
  const cars = useMemo(() => allCars.filter(car => car.visibileCatalogo !== false), [allCars]);
  const { slots: availabilitySlots } = useAvailability(companyId);
  const { settings } = useSettings(companyId);
  // NOTA: la media voto e il conteggio recensioni sono mostrati da
  // <ReviewsSection/> più in basso in questa stessa pagina, che chiama
  // usePublicReviews() in autonomia. Non serve richiamarlo qui: prima lo
  // facevamo per una "striscia di fiducia" nell'hero che non esiste più
  // nel JSX — era rimasta solo la chiamata, un secondo listener real-time
  // che duplicava inutilmente le letture su reviews/{companyId} ad ogni
  // visita della pagina, senza che il risultato venisse mai mostrato.

  // Scroll fluido verso il catalogo, usato dal bottone CTA nell'hero.
  const fleetRef = React.useRef(null);
  const scrollToFleet = () => fleetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // ── Prenotazione diretta dalla pagina pubblica (cliente loggato) ──
  // Il companyId qui è già risolto correttamente dallo slug (a differenza
  // di useCompany(), che risolve companyId solo per gli ADMIN tramite
  // adminIndex/{uid} — un cliente non admin avrebbe companyId sempre null
  // lì). Per questo la prenotazione va aperta qui, non su /account.
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  useEffect(() => {
    if (!user) { setProfileLoaded(true); return; }
    setProfileLoaded(false);
    get(dbRef(database, `users/${user.uid}/profile`))
      .then((snap) => setUserProfile(snap.exists() ? snap.val() : null))
      .catch((err) => console.error('Errore caricamento profilo:', err))
      .finally(() => setProfileLoaded(true));
  }, [user]);

  const isProfileComplete = () => {
    if (!userProfile) return false;
    return userProfile.nome && userProfile.cognome && userProfile.telefono &&
           userProfile.indirizzo && userProfile.citta && userProfile.cap;
  };

  const si = settings?.sito || {};
  const az = settings?.azienda || {};
  // Indirizzo completo per il footer legale (obbligo D.Lgs. 70/2003 art. 7).
  const indirizzoFooter = [az.indirizzo, [az.cap, az.citta].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const accentColor = si.coloreAccento || '#2563EB';
  // Colore "azione" (pulsanti, prezzi, stati attivi): se l'azienda non l'ha
  // personalizzato, ricade sul colore primario — nessun cambiamento visivo
  // per i siti che non hanno ancora impostato un secondo colore.
  const secondaryColor = si.coloreSecondario || accentColor;
  // Testo leggibile sopra i due colori: bianco su sfondi scuri, scuro su
  // sfondi chiari — così un colore chiaro (es. giallo) non rende invisibile
  // il testo dei pulsanti/badge.
  const primaryTextColor = getReadableTextColor(accentColor);
  const secondaryTextColor = getReadableTextColor(secondaryColor);
  // Nome mostrato sul sito pubblico: può essere personalizzato separatamente
  // dalla ragione sociale legale (usata invece su fatture/contratti).
  const displayName = si.nomeSito || ragioneSociale;

  // SEO: title e meta description dinamici per il sito pubblico di QUESTA
  // company — prima erano tutti identici (title generico ereditato da
  // public/index.html), sia sugli slug piattaforma (/tuoslug) sia sui
  // domini personalizzati dei clienti, perché PublicCarList non li
  // impostava mai. Stesso pattern già usato in SearchCompanies.js.
  // Aspetta che companyLoading sia finito per non impostare un title con
  // displayName ancora vuoto, e per non farlo affatto se l'azienda non
  // esiste (pagina "non trovata").
  useEffect(() => {
    if (companyLoading || notFound || !displayName) return;

    const prevTitle = document.title;
    document.title = `${displayName} — Prenota online`;

    const description = si.slogan
      ? `${displayName}: ${si.slogan}. Consulta la disponibilità e prenota online in pochi click.`
      : `${displayName} — noleggio auto e moto. Consulta la disponibilità e prenota online in pochi click.`;

    let metaDesc = document.querySelector('meta[name="description"]');
    let created = false;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
      created = true;
    }
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', description);

    // Open Graph / Twitter: senza questo l'anteprima social di un sito cliente
    // mostrava titolo e descrizione di NoleggioinCloud.
    const social = [
      ['meta[property="og:title"]', document.title],
      ['meta[name="twitter:title"]', document.title],
      ['meta[property="og:description"]', description],
      ['meta[name="twitter:description"]', description],
    ].map(([sel, val]) => {
      const el = document.querySelector(sel);
      const prev = el && el.getAttribute('content');
      if (el) el.setAttribute('content', val);
      return () => { if (el && prev != null) el.setAttribute('content', prev); };
    });

    // Canonical e og:url: l'URL di questa pagina (sul dominio del cliente, o
    // su www.noleggioincloud.app se è la pagina /slug della piattaforma).
    // Senza questo, il canonical resterebbe quello della home di NoleggioinCloud.
    const origin = PLATFORM_HOSTNAMES.has(window.location.hostname)
      && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      ? 'https://www.noleggioincloud.app'
      : window.location.origin;
    const selfUrl = origin + window.location.pathname.replace(/\/+$/, '');
    const canonEl = document.querySelector('link[rel="canonical"]');
    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    const prevCanon = canonEl && canonEl.getAttribute('href');
    const prevOgUrl = ogUrlEl && ogUrlEl.getAttribute('content');
    if (canonEl) canonEl.setAttribute('href', selfUrl);
    if (ogUrlEl) ogUrlEl.setAttribute('content', selfUrl);

    return () => {
      if (canonEl && prevCanon) canonEl.setAttribute('href', prevCanon);
      if (ogUrlEl && prevOgUrl) ogUrlEl.setAttribute('content', prevOgUrl);
      social.forEach(fn => fn());
      document.title = prevTitle;
      if (created) { metaDesc.remove(); } else { metaDesc.setAttribute('content', prevDesc); }
    };
  }, [companyLoading, notFound, displayName, si.slogan]);

  // Azienda inesistente: Firebase risponde 200 (soft 404). noindex + niente
  // canonical verso la home, così Google non la indicizza come duplicato.
  useCompanyPageSEO({ enabled: !companyLoading && !!notFound, noindex: true, title: 'Autonoleggio non trovato' });

  // Toggle visibilità categorie (impostati dall'admin in CatalogoSettings.js).
  // Default a `true` finché settings non è ancora caricato o il campo non esiste,
  // per non far sparire per un istante entrambi i tab al primo render.
  const mostraAuto = settings?.catalogoVisibilita?.mostraAuto !== false;
  const mostraMoto = settings?.catalogoVisibilita?.mostraMoto !== false;

  // Categorie personalizzabili dall'admin in CatalogoSettings.js. Se l'azienda
  // non le ha mai personalizzate, si usa la lista di default globale.
  const categorieAuto = settings?.catalogoVisibilita?.categorieAuto?.length
    ? settings.catalogoVisibilita.categorieAuto
    : CAR_CATEGORIES;
  const categorieMoto = settings?.catalogoVisibilita?.categorieMoto?.length
    ? settings.catalogoVisibilita.categorieMoto
    : MOTO_CATEGORIES;

  const [tapCount, setTapCount] = useState(0);
  const tapTimer = React.useRef(null);
  const handleLogoTap = () => {
    const next = tapCount + 1;
    setTapCount(next);
    clearTimeout(tapTimer.current);
    if (next >= 4) {
      setTapCount(0);
      navigate('/login');
      return;
    }
    tapTimer.current = setTimeout(() => setTapCount(0), 1000);
  };

  const [vehicleType, setVehicleType] = useState('auto');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  // Punto di ritiro/riconsegna scelto già nel catalogo (precompila il modal).
  // La select compare solo se il noleggiatore ha almeno un punto attivo.
  const [ritiroId, setRitiroId] = useState(SEDE_ID);
  const [riconsegnaId, setRiconsegnaId] = useState(SEDE_ID);
  const [riconsegnaDiversa, setRiconsegnaDiversa] = useState(false);
  const sedePoint = useMemo(() => buildSedePoint(settings?.azienda), [settings?.azienda]);
  const activePunti = useMemo(() => getActivePunti(settings?.puntiRitiroRiconsegna), [settings?.puntiRitiroRiconsegna]);
  const puntiScelta = useMemo(() => [sedePoint, ...activePunti], [sedePoint, activePunti]);
  // Con "ritiro e riconsegna nello stesso punto" il cliente paga ENTRAMBI i
  // supplementi (come calcola il server): l'etichetta accanto al nome deve
  // mostrare la somma, non solo il supplemento di ritiro.
  const puntiSceltaStessoPunto = useMemo(() => puntiScelta.map((p) => ({
    ...p,
    supplementoTotale: centsToEuros(eurosToCents(p.supplementoRitiro) + eurosToCents(p.supplementoRiconsegna)),
  })), [puntiScelta]);
  const hasPunti = activePunti.length > 0;
  // Un punto disattivato nel frattempo torna alla sede.
  const validId = (id) => (puntiScelta.some((p) => p.id === id) ? id : SEDE_ID);
  const ritiroScelto = hasPunti ? validId(ritiroId) : SEDE_ID;
  const riconsegnaScelta = hasPunti ? (riconsegnaDiversa ? validId(riconsegnaId) : ritiroScelto) : SEDE_ID;
  const [tabInitialized, setTabInitialized] = useState(false);

  // Appena i settings sono disponibili, se la categoria di default ('auto')
  // è nascosta, sposta l'utente sull'altra categoria visibile. Gira una sola
  // volta (tabInitialized) per non "strappare" via il tab se l'utente ha
  // già cliccato manualmente su AUTO/MOTO nel frattempo.
  useEffect(() => {
    if (tabInitialized || !settings) return;
    if (!mostraAuto && mostraMoto) setVehicleType('moto');
    setTabInitialized(true);
  }, [settings, tabInitialized, mostraAuto, mostraMoto]);

  // Conflict-check basato su availability/ (senza PII) invece che su bookings/ completo.
  const isCarAvailableInPeriod = (carId) => {
    if (!dataInizio || !dataFine) return true;

    const startDate = parseLocalDate(dataInizio);
    const endDate = parseLocalDate(dataFine);

    const hasConflict = availabilitySlots.some(slot => {
      if (slot.carId !== carId) return false;
      if (!STATI_BLOCCANTI.includes(slot.stato)) return false;

      const slotStart = parseLocalDate(slot.dataInizio);
      const slotEnd = parseLocalDate(getEffectiveEndDate(slot));

      return startDate <= slotEnd && endDate > slotStart;
    });

    return !hasConflict;
  };

  const filteredCars = useMemo(() => {
    let result = cars;
    result = result.filter(car => (car.tipoVeicolo || 'auto') === vehicleType && car.disponibile);
    if (filter !== 'all') {
      result = result.filter(car => car.categoria === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(car =>
        `${car.marca || ''} ${car.modello || ''}`.toLowerCase().includes(q)
      );
    }
    if (dataInizio && dataFine) {
      result = result.filter(car => isCarAvailableInPeriod(car.id));
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, vehicleType, filter, searchQuery, dataInizio, dataFine, availabilitySlots]);

  // Solo le categorie che hanno almeno un veicolo disponibile compaiono come
  // filtro: se il noleggio non ha ancora aggiunto veicoli in una categoria
  // personalizzata (o sono tutti non disponibili), quel chip non si mostra.
  const categorieConVeicoli = useMemo(() => {
    const listaCategorie = vehicleType === 'auto' ? categorieAuto : categorieMoto;
    return listaCategorie.filter(cat =>
      cars.some(car => (car.tipoVeicolo || 'auto') === vehicleType && car.categoria === cat && car.disponibile)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, vehicleType, categorieAuto, categorieMoto]);

  // Se il filtro attivo punta a una categoria che è appena sparita (nessun
  // veicolo disponibile), torna a "Tutte" invece di mostrare 0 risultati
  // senza spiegazione.
  useEffect(() => {
    if (filter !== 'all' && !categorieConVeicoli.includes(filter)) {
      setFilter('all');
    }
  }, [filter, categorieConVeicoli]);

  const handleBookCar = (car) => {
    if (!user) {
      navigate(`${basePath}/account`);
      return;
    }
    if (!stripeConnected) {
      return;
    }
    if (!isProfileComplete()) {
      setShowProfileModal(true);
      return;
    }
    setSelectedCar(car);
  };

  if (companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F6FA' }}>
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[var(--brand,#2563EB)] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-slate-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F6FA' }}>
        <div className="text-center max-w-md">
          <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-extrabold text-slate-800 mb-2">Autonoleggio non trovato</h1>
          <p className="text-slate-500 mb-6">
            L'indirizzo che hai visitato non corrisponde a nessun autonoleggio registrato su NoleggioinCloud.
          </p>
          <a href="/" className="text-blue-600 font-semibold hover:underline" style={{ textDecoration: 'none' }}>
            ← Torna a NoleggioinCloud
          </a>
        </div>
      </div>
    );
  }

  if (subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled' || accountBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F6FA' }}>
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-extrabold text-slate-800 mb-2">Sito temporaneamente non disponibile</h1>
          <p className="text-slate-500">
            Questo sito di prenotazione non è al momento raggiungibile. Se sei il titolare di questa attività,
            accedi al pannello per riattivare il servizio.
          </p>
        </div>
      </div>
    );
  }

  return (
    // BUG FIX (accessibilità/contrasto colore personalizzato): i pulsanti
    // AUTO/MOTO e le pillole categoria sotto usavano `text-white` fisso su
    // sfondo bg-[var(--brand,...)] (coloreAccento) — se un'azienda sceglie un
    // colore accento chiaro (es. giallo pallido), il testo bianco diventa
    // quasi illeggibile. `--brand-on` è calcolato qui con la stessa funzione
    // (getReadableTextColor) già usata correttamente per il pulsante "Accedi"
    // e per la CTA dell'Hero — ora riusata anche lì. La differenza non era
    // visibile in SitoPreview.js (l'anteprima nel pannello admin), che
    // calcola già il contrasto giusto per quegli stessi pulsanti: un admin
    // che sceglieva un colore chiaro vedeva un'anteprima corretta ma il sito
    // pubblico reale mostrava testo bianco illeggibile.
    <div className="min-h-screen" style={{background:"#F5F6FA", '--brand': accentColor, '--brand-secondary': secondaryColor, '--brand-on': primaryTextColor, '--brand-secondary-on': secondaryTextColor}}>
      <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0" onClick={handleLogoTap}>
            {(si.logoSito || az.logoAzienda) ? (
              <img
                src={si.logoSito || az.logoAzienda}
                alt={displayName}
                className="h-7 sm:h-8 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <BrandDefaultIcon className="text-[var(--brand,#2563EB)]" />
            )}
            <div className="flex flex-col justify-center leading-tight">
              <span className="text-base sm:text-lg font-display font-extrabold text-[var(--brand,#2563EB)] tracking-tight leading-none">
                {displayName}
              </span>
              {si.slogan && (
                <span className="hidden sm:block text-[11px] font-medium text-slate-400 tracking-normal leading-none mt-[3px]">
                  {si.slogan}
                </span>
              )}
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <LanguageToggle />
            <div className="w-px h-5 bg-slate-200"></div>
            <Link to={`${basePath}/contatti`}
              className="px-4 py-2 text-slate-500 font-semibold text-sm hover:text-[var(--brand,#2563EB)] hover:bg-slate-50 rounded-lg transition"
              style={{ textDecoration: 'none' }}>
              Contatti
            </Link>
            <Link to={`${basePath}/account`}
              className="px-5 py-2 border font-semibold text-sm hover:bg-slate-50 rounded-full transition"
              style={{ textDecoration: 'none', borderColor: secondaryColor, color: secondaryColor }}>
              Accedi
            </Link>
          </div>

          <div className="flex sm:hidden items-center gap-1.5">
            <LanguageToggle />
            <div className="w-px h-4 bg-slate-200"></div>
            <Link to={`${basePath}/contatti`} className="px-2 py-2 text-slate-500 text-xs font-medium" style={{ textDecoration: 'none' }}>
              Contatti
            </Link>
            <Link to={`${basePath}/account`} className="px-3 py-2 rounded-full text-xs font-bold"
              style={{ textDecoration: 'none', background: secondaryColor, color: secondaryTextColor }}>
              Accedi
            </Link>
          </div>
        </div>
      </div>

      {!stripeConnected && (
        <div className="bg-amber-50 border-b border-amber-200 py-2.5 px-4 text-center">
          <p className="text-xs sm:text-sm text-amber-800">
            Le prenotazioni online non sono ancora attive per questo autonoleggio.{' '}
            <Link
              to={`${basePath}/contatti`}
              className="font-semibold underline hover:text-amber-900 transition-colors"
              style={{ textDecoration: 'underline' }}
            >
              Contatta l&#39;azienda direttamente per prenotare.
            </Link>
          </p>
        </div>
      )}

      <HeroSection
        displayName={displayName}
        slogan={si.slogan}
        bannerImage={si.bannerImage}
        accentColor={accentColor}
        secondaryColor={secondaryColor}
        heroTema={si.heroTema || 'cerchi'}
        onCtaClick={scrollToFleet}
      />

      <div ref={fleetRef} className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 scroll-mt-16">
        <div className="mb-8">
          <div className="flex justify-center gap-3 mb-6">
            {mostraAuto && (
              <button
                onClick={() => { setVehicleType('auto'); setFilter('all'); }}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition border ${
                  vehicleType === 'auto'
                    ? 'bg-[var(--brand,#2563EB)] text-[var(--brand-on,#fff)] border-[var(--brand,#2563EB)]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-[var(--brand,#2563EB)] hover:text-[var(--brand,#2563EB)]'
                }`}
              >
                <Car className="w-4 h-4" />
                AUTO ({cars.filter(c => (c.tipoVeicolo || 'auto') === 'auto' && c.disponibile).length})
              </button>
            )}
            {mostraMoto && (
              <button
                onClick={() => { setVehicleType('moto'); setFilter('all'); }}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition border ${
                  vehicleType === 'moto'
                    ? 'bg-[var(--brand,#2563EB)] text-[var(--brand-on,#fff)] border-[var(--brand,#2563EB)]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-[var(--brand,#2563EB)] hover:text-[var(--brand,#2563EB)]'
                }`}
              >
                <MotoIcon className="w-4 h-4" />
                MOTO ({cars.filter(c => c.tipoVeicolo === 'moto' && c.disponibile).length})
              </button>
            )}
          </div>

          {!mostraAuto && !mostraMoto ? (
            <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-200 mb-6">
              <p className="text-slate-500 text-sm font-semibold">Catalogo momentaneamente non disponibile.</p>
              <p className="text-slate-400 text-xs mt-1">Contatta l'azienda per maggiori informazioni.</p>
            </div>
          ) : (
          <>
          <div className="flex justify-center flex-wrap gap-2 pb-1">
            <button
              onClick={() => setFilter('all')}
              className={`flex-shrink-0 px-4 sm:px-5 py-1.5 rounded-full font-bold text-xs tracking-wide transition border ${
                filter === 'all'
                  ? 'bg-[var(--brand,#2563EB)] text-[var(--brand-on,#fff)] border-[var(--brand,#2563EB)]'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-[var(--brand,#2563EB)] hover:text-[var(--brand,#2563EB)]'
              }`}
            >
              Tutte ({cars.filter(c => (c.tipoVeicolo || 'auto') === vehicleType && c.disponibile).length})
            </button>
            {categorieConVeicoli.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`flex-shrink-0 px-4 sm:px-5 py-1.5 rounded-full font-bold text-xs tracking-wide transition capitalize border ${
                  filter === cat
                    ? 'bg-[var(--brand,#2563EB)] text-[var(--brand-on,#fff)] border-[var(--brand,#2563EB)]'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-[var(--brand,#2563EB)] hover:text-[var(--brand,#2563EB)]'
                }`}
              >
                {cat} ({cars.filter(c => (c.tipoVeicolo || 'auto') === vehicleType && c.categoria === cat && c.disponibile).length})
              </button>
            ))}
          </div>
          </>
          )}

          <div className="max-w-md mx-auto mt-4 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Cerca per marca o modello, es. Panda, Tiguan..."
              className="w-full pl-10 pr-9 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[var(--brand,#2563EB)] focus:border-transparent"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Cancella ricerca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="mt-5 bg-slate-50 rounded-2xl border border-slate-200 p-4 sm:p-6 overflow-hidden">
            <div className="flex items-center gap-2 mb-5">
              <Calendar className="w-4 h-4 text-[var(--brand,#2563EB)]" />
              <h3 className="text-sm font-display font-bold text-slate-800 uppercase tracking-widest">Filtra per Disponibilità</h3>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Data Inizio Noleggio
                </label>
                <input
                  type="date"
                  value={dataInizio}
                  onChange={(e) => setDataInizio(e.target.value)}
                  min={toLocalISODate()}
                  className="w-full max-w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-700 text-sm focus:outline-none focus:border-[var(--brand,#2563EB)] focus:ring-1 focus:ring-[var(--brand,#2563EB)] transition bg-white box-border"
                />
              </div>

              <div className="flex-1">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Data Fine Noleggio
                </label>
                <input
                  type="date"
                  value={dataFine}
                  onChange={(e) => setDataFine(e.target.value)}
                  min={dataInizio || toLocalISODate()}
                  className="w-full max-w-full px-4 py-3 border border-slate-200 rounded-xl text-slate-700 text-sm focus:outline-none focus:border-[var(--brand,#2563EB)] focus:ring-1 focus:ring-[var(--brand,#2563EB)] transition bg-white box-border"
                />
              </div>
            </div>

            {hasPunti && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <PuntoCombobox
                      id="catalogo-ritiro"
                      label={riconsegnaDiversa ? 'Dove ritiri' : 'Dove ritiri e riconsegni'}
                      points={riconsegnaDiversa ? puntiScelta : puntiSceltaStessoPunto}
                      value={ritiroScelto}
                      onChange={setRitiroId}
                      supplementKey={riconsegnaDiversa ? 'supplementoRitiro' : 'supplementoTotale'}
                    />
                  </div>
                  {riconsegnaDiversa && (
                    <div className="flex-1">
                      <PuntoCombobox
                        id="catalogo-riconsegna"
                        label="Dove riconsegni"
                        points={puntiScelta}
                        value={riconsegnaScelta}
                        onChange={setRiconsegnaId}
                        supplementKey="supplementoRiconsegna"
                      />
                    </div>
                  )}
                </div>
                <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={riconsegnaDiversa}
                    onChange={(e) => {
                      setRiconsegnaDiversa(e.target.checked);
                      if (e.target.checked) setRiconsegnaId(ritiroScelto);
                    }}
                    className="rounded border-slate-300"
                  />
                  Riconsegna in un punto diverso
                </label>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              {dataInizio && dataFine && (
                <div className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <p className="text-sm text-slate-700">
                    <strong className="flex items-center gap-1"><CalendarDays className="w-4 h-4 inline" /> Periodo:</strong> {parseLocalDate(dataInizio).toLocaleDateString('it-IT')} - {parseLocalDate(dataFine).toLocaleDateString('it-IT')}
                    <span className="ml-2">
                      ({Math.ceil((parseLocalDate(dataFine) - parseLocalDate(dataInizio)) / (1000 * 60 * 60 * 24))} {Math.ceil((parseLocalDate(dataFine) - parseLocalDate(dataInizio)) / (1000 * 60 * 60 * 24)) === 1 ? 'giorno' : 'giorni'})
                    </span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Mostrando solo le auto disponibili in questo periodo</span>
                  </p>
                </div>
              )}
              {(dataInizio || dataFine) && (
                <button
                  onClick={() => { setDataInizio(''); setDataFine(''); }}
                  className="px-5 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition font-medium whitespace-nowrap text-sm"
                >
                  Cancella date
                </button>
              )}
            </div>
          </div>
        </div>

        {!carsLoading && filteredCars.length > 0 && (vehicleType === 'auto' ? mostraAuto : mostraMoto) && (
          <div className="mb-6 mt-8">
            <h2 className="text-2xl sm:text-3xl font-display font-extrabold text-slate-900 tracking-tight mb-2">
              {vehicleType === 'moto' ? 'La nostra flotta moto' : 'La nostra flotta auto'}
            </h2>
            <div className="w-12 h-1 bg-[var(--brand,#2563EB)] rounded-full"></div>
          </div>
        )}

        {(vehicleType === 'auto' ? !mostraAuto : !mostraMoto) ? null : carsLoading ? (
          // Skeleton che ricalca la vera CarCard (immagine, titolo, badge,
          // prezzo, bottone) invece di un semplice spinner: dà subito
          // un'idea della forma della pagina e si percepisce come più veloce.
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="rounded-2xl border border-slate-100 bg-white overflow-hidden animate-pulse">
                <div className="h-44 bg-slate-100" />
                <div className="p-4 sm:p-5">
                  <div className="h-4 w-2/3 bg-slate-100 rounded mb-3" />
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="h-12 bg-slate-100 rounded-xl" />
                    <div className="h-12 bg-slate-100 rounded-xl" />
                    <div className="h-12 bg-slate-100 rounded-xl" />
                  </div>
                  <div className="h-5 w-1/3 bg-slate-100 rounded mb-4" />
                  <div className="h-10 bg-slate-100 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-16 text-center">
            {vehicleType === 'moto'
              ? <MotoIcon className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              : <Car className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            }
            <p className="text-gray-500 text-xl mb-2">
              {searchQuery.trim()
                ? `Nessuna ${vehicleType === 'moto' ? 'moto' : 'auto'} trovata per "${searchQuery.trim()}"`
                : dataInizio && dataFine
                ? `Nessuna ${vehicleType === 'moto' ? 'moto' : 'auto'} disponibile per il periodo selezionato`
                : `Nessuna ${vehicleType === 'moto' ? 'moto' : 'auto'} disponibile al momento`}
            </p>
            {searchQuery.trim() ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-[var(--brand,#2563EB)] text-sm font-semibold mt-2 hover:underline"
              >
                Cancella la ricerca
              </button>
            ) : dataInizio && dataFine && (
              <p className="text-gray-400 text-sm mt-2">
                Prova a cambiare le date o rimuovere il filtro periodo
              </p>
            )}
          </div>
        ) : (
          <>
            {(dataInizio && dataFine) && (
              <div className="mb-4 text-center">
                <p className="text-gray-600">
                  <strong>{filteredCars.length}</strong> {filteredCars.length === 1
                    ? (vehicleType === 'moto' ? 'moto disponibile' : 'auto disponibile')
                    : (vehicleType === 'moto' ? 'moto disponibili' : 'auto disponibili')
                  } per il periodo selezionato
                </p>
              </div>
            )}

            <CarsPagination
              cars={filteredCars}
              renderCarCard={(car) => (
                <CarCard
                  key={car.id}
                  car={car}
                  showBookButton={stripeConnected}
                  onSelect={() => handleBookCar(car)}
                />
              )}
            />
          </>
        )}
      </div>

      <ReviewsSection companyId={companyId} accentColor={accentColor} />

      <div
        className="relative overflow-hidden text-white py-14 mt-10"
        style={{
          background: `linear-gradient(135deg, ${accentColor} 0%, color-mix(in srgb, ${accentColor} 65%, black) 100%)`
        }}
      >
        {/* decorazione grafica leggera, puramente estetica */}
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/5"></div>
        <div className="pointer-events-none absolute -left-10 -bottom-24 w-72 h-72 rounded-full bg-white/5"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="flex items-start gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Car className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-display font-extrabold leading-tight">{displayName}</h3>
                {si.slogan && <p className="text-white/70 max-w-md mt-1">{si.slogan}</p>}
                {si.footerSottotitolo && (
                  <p className="text-white/60 text-sm max-w-md mt-2">{si.footerSottotitolo}</p>
                )}
              </div>
            </div>
            <Link
              to={`${basePath}/contatti`}
              className="inline-flex items-center justify-center gap-2 self-start md:self-auto px-6 py-3 rounded-full bg-white font-bold text-sm hover:bg-white/90 transition shadow-lg shadow-black/10 flex-shrink-0"
              style={{ textDecoration: 'none', color: accentColor }}
            >
              Contatti e recapiti
            </Link>
          </div>
        </div>
      </div>

      <PublicFooter
        displayName={displayName}
        slogan={si.slogan}
        logoSito={si.logoSito || az.logoAzienda || ''}
        footerSottotitolo={si.footerSottotitolo}
        accentColor={accentColor}
        basePath={basePath}
        ragioneSociale={az.ragioneSociale}
        piva={az.piva}
        indirizzo={indirizzoFooter}
        email={az.email}
        telefono={az.telefono}
      />

      {showProfileModal && user && profileLoaded && (
        <ProfileModal
          user={user}
          profile={userProfile}
          allowClose={!!userProfile}
          onClose={() => { if (userProfile) setShowProfileModal(false); }}
          onSave={(newProfile) => {
            setUserProfile(newProfile);
            setShowProfileModal(false);
          }}
        />
      )}

      {selectedCar && user && isProfileComplete() && (
        <BookingModal
          car={selectedCar}
          user={user}
          userProfile={userProfile}
          companyId={companyId}
          companySlug={companySlug}
          companyDisplayName={displayName}
          allBookings={availabilitySlots}
          initialRitiroId={ritiroScelto}
          initialRiconsegnaId={riconsegnaScelta}
          onClose={() => setSelectedCar(null)}
          onSuccess={() => { setSelectedCar(null); navigate(`${basePath}/account`); }}
        />
      )}
    </div>
  );
};

export default PublicCarList;
